import React, { useState, useEffect } from 'react';
import {
  Bell,
  CheckCircle,
  AlertCircle,
  Info,
  Star,
  Mail,
  MessageSquare,
  Users,
  FileText,
  Calendar,
  Clock,
  Trash2,
  CheckCheck,
  Eye,
  EyeOff,
  Settings,
  Filter,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  Download,
  Archive,
  UserPlus,
  Tag,
  Check,
  XIcon
} from 'lucide-react';
import './Notifications.css';
import { ApiUrl } from '../API/ApiUrl';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';

const Notifications = () => {
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 10;

  const [notifications, setNotifications] = useState([]);

  const [selectedNotifications, setSelectedNotifications] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);

  const notificationIcons = {
    submission: { icon: FileText, color: "#2563eb", path: "/admin/online-submissions" },
    reviewer: { icon: UserPlus, color: "#9333ea", path: "/admin/reviewer-submissions" },
    contact: { icon: Mail, color: "#16a34a", path: "/admin/contacts" },
    download: { icon: Download, color: "#ea580c", path: "/admin/downloads" }
  };

  const fetchNotifications = async () => {
    try {

      const token = localStorage.getItem("adminToken");

      const response = await fetch(`${ApiUrl}/notifications`, {
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      const data = await response.json();

      if (!response.ok) return;

      setNotifications(data);

      // calculate unread
      const unread = data.filter(n => n.is_read === 0).length;
      setUnreadCount(unread);

    } catch (error) {
      console.error("Notification fetch error:", error);
    }
  };

  useEffect(() => {

    fetchNotifications();

    const handleUpdate = () => {
      fetchNotifications();
    };

    window.addEventListener("notificationsUpdated", handleUpdate);

    return () => {
      window.removeEventListener("notificationsUpdated", handleUpdate);
    };

  }, []);

  // Filter notifications
  const filteredNotifications = notifications.filter(notif => {
    // Filter by type
    if (filter !== 'all' && notif.category !== filter) return false;

    // Search filter
    if (searchTerm) {
      const searchLower = searchTerm.toLowerCase();
      return (
        notif.title.toLowerCase().includes(searchLower) ||
        notif.message.toLowerCase().includes(searchLower) ||
        notif.type.toLowerCase().includes(searchLower)
      );
    }

    return true;
  });

  // Pagination
  const totalPages = Math.ceil(filteredNotifications.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const paginatedNotifications = filteredNotifications.slice(startIndex, startIndex + itemsPerPage);

  // Stats
  // const unreadCount = notifications.filter(n => !n.read).length;
  const highPriorityCount = notifications.filter(n => n.priority === 'high' && !n.read).length;

  // Handlers
  const handleMarkAsRead = (id) => {
    setNotifications(notifications.map(notif =>
      notif.id === id ? { ...notif, read: true } : notif
    ));
  };

  const handleMarkAllAsRead = () => {
    setNotifications(notifications.map(notif => ({ ...notif, read: true })));
    setSelectedNotifications([]);
    setSelectAll(false);
  };

  const handleDelete = (id) => {
    if (window.confirm('Are you sure you want to delete this notification?')) {
      setNotifications(notifications.filter(notif => notif.id !== id));
    }
  };

  const handleDeleteSelected = () => {
    if (selectedNotifications.length === 0) return;
    if (window.confirm(`Are you sure you want to delete ${selectedNotifications.length} notifications?`)) {
      setNotifications(notifications.filter(notif => !selectedNotifications.includes(notif.id)));
      setSelectedNotifications([]);
      setSelectAll(false);
    }
  };

  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedNotifications([]);
    } else {
      setSelectedNotifications(paginatedNotifications.map(n => n.id));
    }
    setSelectAll(!selectAll);
  };

  const handleSelect = (id) => {
    if (selectedNotifications.includes(id)) {
      setSelectedNotifications(selectedNotifications.filter(nId => nId !== id));
      setSelectAll(false);
    } else {
      setSelectedNotifications([...selectedNotifications, id]);
    }
  };

  const getPriorityBadge = (priority) => {
    const config = {
      high: { class: 'priority-high', label: 'High' },
      medium: { class: 'priority-medium', label: 'Medium' },
      low: { class: 'priority-low', label: 'Low' }
    };
    return <span className={`priority-badge ${config[priority].class}`}>{config[priority].label}</span>;
  };

  const formatDate = (date) => {
    const d = new Date(date);

    return {
      date: d.toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric"
      }),
      time: d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
    };
  };


  const markAllRead = async () => {

    try {

      const token = localStorage.getItem("adminToken");

      await fetch(`${ApiUrl}/notifications/read-all`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      fetchNotifications();
      window.dispatchEvent(new Event("notificationsUpdated"));

    } catch (error) {
      console.error(error);
    }

  };

  const markAsRead = async (id) => {

    try {

      const token = localStorage.getItem("adminToken");

      await fetch(`${ApiUrl}/notifications/${id}/read`, {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        }
      });

      fetchNotifications();
      window.dispatchEvent(new Event("notificationsUpdated"));


    } catch (error) {
      console.error(error);
    }

  };


  const handleDeleteNotification = async (ids) => {

    if (!Array.isArray(ids)) {
      ids = [ids];
    }

    const confirm = await Swal.fire({
      title: "Delete Notifications?",
      text: `Delete ${ids.length} selected notifications?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#e3342f",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete"
    });

    if (!confirm.isConfirmed) return;

    try {

      const token = localStorage.getItem("adminToken");

      // delete all selected notifications
      await Promise.all(
        ids.map((id) =>
          fetch(`${ApiUrl}/notifications/${id}`, {
            method: "DELETE",
            headers: {
              Authorization: `Bearer ${token}`,
            }
          })
        )
      );

      await Swal.fire({
        icon: "success",
        title: "Deleted",
        text: "Notifications deleted successfully",
        timer: 1500,
        showConfirmButton: false
      });

      // update UI instantly
      setNotifications((prev) =>
        prev.filter((n) => !ids.includes(n.id))
      );

      setSelectedNotifications([]);
      setSelectAll(false);
    } catch (error) {

      console.error(error);

      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to delete notifications"
      });

    }

  };

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [totalPages]);

  return (
    <div className="notifications-page">
      {/* Header */}
      <div className="page-header">
        <div className="title-section">
          <Bell size={24} className="title-icon" />
          <div>
            <h1 className="page-title">Notifications</h1>
            <p className="page-subtitle">Stay updated with all journal activities</p>
          </div>
        </div>

        <div className="header-actions">

          <button className="btn-primary" onClick={markAllRead}>
            <CheckCheck size={16} />
            <span>Mark All Read</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="stats-grid">
        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#eff6ff' }}>
            <Bell size={20} style={{ color: '#3b82f6' }} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Total</span>
            <span className="stat-value">{notifications.length}</span>
          </div>
        </div>

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fee2e2' }}>
            <AlertCircle size={20} style={{ color: '#ef4444' }} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Unread</span>
            <span className="stat-value">{unreadCount}</span>
          </div>
        </div>

        {/* <div className="stat-card">
          <div className="stat-icon" style={{ background: '#fef3c7' }}>
            <Flag size={20} style={{ color: '#f59e0b' }} />
          </div>
          <div className="stat-content">
            <span className="stat-label">High Priority</span>
            <span className="stat-value">{highPriorityCount}</span>
          </div>
        </div> */}

        <div className="stat-card">
          <div className="stat-icon" style={{ background: '#e6f7f0' }}>
            <CheckCircle size={20} style={{ color: '#10b981' }} />
          </div>
          <div className="stat-content">
            <span className="stat-label">Read</span>
            <span className="stat-value">{notifications.length - unreadCount}</span>
          </div>
        </div>
      </div>



      {/* Filter Bar */}
      <div className="filter-bar">
        {/* <div className="filter-tabs">
          <button
            className={`filter-tab ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            All
          </button>
          <button
            className={`filter-tab ${filter === 'submissions' ? 'active' : ''}`}
            onClick={() => setFilter('submissions')}
          >
            Submissions
          </button>
          <button
            className={`filter-tab ${filter === 'reviews' ? 'active' : ''}`}
            onClick={() => setFilter('reviews')}
          >
            Reviews
          </button>
          <button
            className={`filter-tab ${filter === 'messages' ? 'active' : ''}`}
            onClick={() => setFilter('messages')}
          >
            Messages
          </button>
          <button
            className={`filter-tab ${filter === 'deadlines' ? 'active' : ''}`}
            onClick={() => setFilter('deadlines')}
          >
            Deadlines
          </button>
        </div> */}

        <div className="search-wrapper">
          <Search size={16} className="search-icon" />
          <input
            type="text"
            placeholder="Search notifications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          {searchTerm && (
            <XIcon
              size={16}
              className="clear-icon"
              onClick={() => setSearchTerm("")}
            />
          )}
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedNotifications.length > 0 && (
        <div className="bulk-actions">
          <span className="selected-count">{selectedNotifications.length} selected</span>
          {/* <button className="bulk-btn" onClick={markAllRead}>
            <CheckCheck size={14} />
            <span>Mark Read</span>
          </button> */}
          <button className="bulk-btn" onClick={() => handleDeleteNotification(selectedNotifications)}>
            <Trash2 size={14} />
            <span>Delete</span>
          </button>
          <button className="bulk-btn" onClick={() => {
            setSelectedNotifications([]);
            setSelectAll(false);
          }}>
            <X size={14} />
            <span>Clear</span>
          </button>
        </div>
      )}

      <div className="notifications-container">
        <div className="notifications-header">
          <div className="checkbox-col">
            <label className="checkbox-label">
              <input
                type="checkbox"
                checked={selectAll}
                onChange={handleSelectAll}
              />
              <span className="checkbox-custom"></span>
            </label>
          </div>
          <div className="type-col">Type</div>
          <div className="content-col">Notification</div>
          {/* <div className="priority-col">Priority</div> */}
          <div className="time-col">Date</div>
          <div className="time-col">Time</div>
          <div className="time-col">View</div>

          <div className="time-col">Delete</div>
          {/* <div className="actions-col">Actions</div> */}
        </div>

        <div className="notifi-list">
          {paginatedNotifications.map((notification) => {

            const iconData = notificationIcons[notification.type] || {};
            const Icon = iconData.icon || Tag;
            const color = iconData.color || "#64748b";
            const path = iconData.path || "#"

            const { date, time } = formatDate(notification.created_at);

            const isSelected = selectedNotifications.includes(notification.id);

            return (
              <div
                key={notification.id}
                className={`notifi-item ${!notification.is_read ? "unread" : ""} ${isSelected ? "selected" : ""}`}
              >

                {/* Checkbox */}
                <div className="checkbox-col">
                  <label className="checkbox-label">
                    <input
                      type="checkbox"
                      checked={isSelected}
                      onChange={() => handleSelect(notification.id)}
                    />
                    <span className="checkbox-custom"></span>
                  </label>
                </div>

                {/* Type Icon */}
                <div
                  className="type-icon"
                  style={{ background: `${color}15` }}
                >
                  <Icon size={18} style={{ color }} />
                </div>

                {/* Content */}
                <div className="content-col">
                  <div className="notification-content">

                    <div className="notification-title">
                      <h4>{notification.title}</h4>
                      {!notification.is_read && (
                        <span className="unread-badge">New</span>
                      )}
                    </div>

                    <p className="notification-message">
                      {notification.message}
                    </p>

                  </div>
                </div>

                {/* Date */}
           
                <div className="time-col">
                  <div className="time-display">
                    <Calendar size={12} />
                    <span>{date}</span>
                  </div>
                </div>

                {/* Time */}
                <div className="time-col">
                  <div className="time-display">
                    <Clock size={12} />
                    <span>{time}</span>
                  </div>
                </div>
                     <div className="time-col">
                  <div className="time-display">
                    <Link to={path} >
                      <button
                        className="action-btn"
                        onClick={() => !notification.is_read && markAsRead(notification.id)}

                      >
                        <Eye size={12} />
                      </button>
                    </Link>
                  </div>
                </div>

                {/* Actions */}
                <div className="actions-col">
                  <div className="action-buttons">


                    {/* <button
                      className="action-btn"
                      onClick={() => !notification.is_read && markAsRead(notification.id)}
                      title={notification.is_read ? "Already read" : "Mark as read"}
                    >
                      {notification.is_read ? (
                        <CheckCheck size={14} />
                      ) : (
                        <Check size={14} />
                      )}
                    </button> */}


                    <button
                      className="action-btn delete"
                      onClick={() => handleDeleteNotification(notification.id)}
                      title="Delete"
                    >
                      <Trash2 size={14} />
                    </button>

                  </div>
                </div>

              </div>
            );
          })}
        </div>

        {/* Empty State */}
        {filteredNotifications.length === 0 && (
          <div className="empty-state">
            <Bell size={48} />
            <h3>No notifications found</h3>
            <p>You're all caught up! Check back later for new notifications.</p>
          </div>
        )}

        {/* Pagination */}
        {filteredNotifications.length > 0 && (
          <div className="pagination">
            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
            </button>

            <span className="page-info">
              Page {currentPage} of {totalPages}
            </span>

            <button
              className="pagination-btn"
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
              disabled={currentPage === totalPages}
            >
              <ChevronRight size={16} />
            </button>
          </div>
        )}
      </div>

      {/* Footer Summary */}
      <div className="notifications-footer">
        <div className="footer-summary">
          <span>Total: {notifications.length} notifications</span>
          <span className="dot">•</span>
          <span>Unread: {unreadCount}</span>
          <span className="dot">•</span>
          {/* <span>High Priority: {highPriorityCount}</span> */}
        </div>
        {/* <div className="footer-actions">
          <button className="footer-btn" onClick={handleMarkAllAsRead}>
            <CheckCheck size={14} />
            <span>Mark all read</span>
          </button>
          <button className="footer-btn">
            <Archive size={14} />
            <span>Archive all</span>
          </button>
        </div> */}
      </div>
    </div>
  );
};

export default Notifications;